import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, ShoppingCart, X, Pause, MessageCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const RaffleDetailPage = () => {
  const { raffleId } = useParams();
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [tappedEntry, setTappedEntry] = useState(null);
  const [statusFilter, setStatusFilter] = useState('available');
  const [reserving, setReserving] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(null);

  const [buyerData, setBuyerData] = useState({ name: '', phone: '' });

  const formRef = useRef(null);
  const cartRef = useRef(null);

  useEffect(() => {
    fetchRaffle();
  }, [raffleId]);

  const fetchRaffle = async () => {
    try {
      const response = await axios.get(`${API}/raffles/${raffleId}`);
      setRaffle(response.data);

      if (response.data.admin_slug) {
        try {
          const adminResponse = await axios.get(`${API}/admins/public/${response.data.admin_slug}`);
          setAdminInfo(adminResponse.data);
        } catch (e) {}
      }
    } catch (error) {
      console.error('Error fetching raffle:', error);
      toast.error('Error al cargar el sorteo');
    } finally {
      setLoading(false);
    }
  };

  const getBackgroundStyle = () => {
    const primaryColor = adminInfo?.primary_color || '#9333ea';
    const backgroundImage = adminInfo?.background_image;
    if (backgroundImage) {
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${backgroundImage})`,
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      };
    }
    const secondaryColor = adjustColor(primaryColor, -30);
    const tertiaryColor = adjustColor(primaryColor, 40);
    return { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 50%, ${tertiaryColor} 100%)` };
  };

  function adjustColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(Math.min((num >> 16) + amt, 255), 0);
    const G = Math.max(Math.min((num >> 8 & 0x00FF) + amt, 255), 0);
    const B = Math.max(Math.min((num & 0x0000FF) + amt, 255), 0);
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  }

  const getPrimaryColor = () => adminInfo?.primary_color || '#9333ea';

  const handleEntryClick = (entry) => {
    if (entry.status !== 'available') {
      if (entry.buyer_name) {
        setTappedEntry(entry);
        setTimeout(() => setTappedEntry(null), 3000);
      }
      return;
    }
    const isSelected = selectedEntries.find(e => e.id === entry.id);
    if (isSelected) {
      setSelectedEntries(selectedEntries.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries([...selectedEntries, entry]);
      if (selectedEntries.length === 0) {
        setTimeout(() => cartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      }
    }
  };

  const handleReserve = async () => {
    if (selectedEntries.length === 0) {
      toast.error('Selecciona al menos un numero');
      return;
    }
    if (!buyerData.name || !buyerData.phone) {
      toast.error('Completa todos los datos');
      return;
    }
    setReserving(true);
    try {
      const response = await axios.post(`${API}/raffles/${raffleId}/reserve`, {
        raffle_id: raffleId,
        entry_ids: selectedEntries.map(e => e.id),
        buyer_name: buyerData.name,
        buyer_phone: buyerData.phone
      });
      const { whatsapp_number, whatsapp_message } = response.data;
      const cleanNumber = whatsapp_number.replace(/[^0-9]/g, '');
      const encodedMessage = encodeURIComponent(whatsapp_message);
      const waUrl = `https://api.whatsapp.com/send?phone=${cleanNumber}&text=${encodedMessage}`;

      setReservationSuccess({
        numbers: selectedEntries.map(e => e.number),
        total: selectedEntries.length * raffle.unit_price,
        whatsappUrl: waUrl
      });
      fetchRaffle();
      setSelectedEntries([]);
      setShowForm(false);
      setBuyerData({ name: '', phone: '' });
    } catch (error) {
      console.error('Error reserving:', error);
      toast.error(error.response?.data?.detail || 'Error al reservar');
    } finally {
      setReserving(false);
    }
  };

  const handleCancel = () => {
    setSelectedEntries([]);
    setShowForm(false);
    setBuyerData({ name: '', phone: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="text-white text-2xl">Cargando...</div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="text-white text-2xl">Sorteo no encontrado</div>
      </div>
    );
  }

  if (raffle.is_active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={getBackgroundStyle()}>
        <Card className="max-w-md bg-white/95 backdrop-blur-lg">
          <CardContent className="pt-6 text-center">
            <Pause size={64} className="mx-auto mb-4 text-yellow-500" />
            <h2 className="text-2xl font-bold mb-2">Sorteo Pausado</h2>
            <p className="text-gray-600">Este sorteo no esta disponible en este momento</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPrice = selectedEntries.length * raffle.unit_price;
  const backRoute = raffle.admin_slug ? `/rifas/${raffle.admin_slug}` : '/';

  return (
    <div className="min-h-screen p-4 md:p-8" style={getBackgroundStyle()}>
      <div className="max-w-7xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate(backRoute)}
          className="mb-6 bg-white/10 text-white hover:bg-white/20 border-white/30"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver a Sorteos
        </Button>

        {/* Cabecera del Sorteo */}
        <Card className="bg-white/95 backdrop-blur-lg mb-6">
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-6">
              {raffle.logo && (
                <div className="w-full md:w-48">
                  <img src={raffle.logo} alt={raffle.title} className="w-full h-32 object-contain" />
                </div>
              )}
              <div className="flex-1">
                <CardTitle className="text-3xl mb-4" data-testid="raffle-title">{raffle.title}</CardTitle>
                <div className="space-y-2">
                  <p className="text-lg">
                    <strong>Valor numero/fila:</strong>{' '}
                    <span className="text-green-600 font-bold">${raffle.unit_price}</span>
                  </p>
                  <p><strong>Alias de pago:</strong> {raffle.payment_alias}</p>
                  {raffle.payment_holder_name && (
                    <p><strong>A nombre de:</strong> {raffle.payment_holder_name}</p>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {raffle.observations && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-semibold mb-1 flex items-center gap-2 text-amber-800">
                    📝 Observaciones
                  </h3>
                  <p className="text-gray-700 whitespace-pre-wrap text-sm" data-testid="raffle-observations">
                    {raffle.observations}
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-xl font-semibold mb-2">Premios:</h3>
                <ul className="list-disc list-inside space-y-1">
                  {raffle.prizes.map((prize, idx) => (
                    <li key={idx} className="text-gray-700">{prize}</li>
                  ))}
                </ul>
              </div>
              {raffle.prize_images && raffle.prize_images.length > 0 && (
                <div>
                  <h3 className="text-xl font-semibold mb-2">Imagenes de premios:</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {raffle.prize_images.map((img, idx) => (
                      <img key={idx} src={img} alt={`Premio ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Grid de numeros */}
        <Card className="bg-white/95 backdrop-blur-lg mb-6">
          <CardHeader>
            <CardTitle>Numeros del sorteo</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              {[
                { value: 'available', label: 'Disponibles', color: 'bg-green-500', activeText: 'text-white', count: raffle.entries.filter(e => e.status === 'available').length },
                { value: 'reserved', label: 'Reservados', color: 'bg-amber-500', activeText: 'text-white', count: raffle.entries.filter(e => e.status === 'reserved').length },
                { value: 'paid', label: 'Pagados', color: 'bg-purple-500', activeText: 'text-white', count: raffle.entries.filter(e => e.status === 'paid').length },
              ].map(f => (
                <button
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all w-full sm:w-auto ${
                    statusFilter === f.value ? `${f.color} ${f.activeText} shadow-md` : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-${f.value}`}
                >
                  <div className={`w-3 h-3 rounded-full ${statusFilter === f.value ? 'bg-white/50' : f.color}`}></div>
                  {f.label} ({f.count})
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Lineas filtradas */}
              {raffle.entries.filter(e => e.status === statusFilter && e.display_numbers && e.display_numbers.length > 0).map((entry) => {
                const isSelected = selectedEntries.find(e => e.id === entry.id);
                const statusClass = entry.status === 'available' ? 'raffle-number available' : entry.status === 'reserved' ? 'raffle-number reserved' : 'raffle-number paid';
                return (
                  <div key={entry.id} className="relative">
                    <button
                      onClick={() => handleEntryClick(entry)}
                      className={`${statusClass} ${isSelected ? 'selected' : ''} w-full p-3 rounded-lg font-semibold text-sm flex items-center justify-between`}
                      data-testid={`entry-${entry.number}`}
                    >
                      <span className="font-bold">{entry.number}</span>
                      <span className="text-xs opacity-90 ml-2">{entry.display_numbers.join(' - ')}</span>
                      {entry.buyer_name && <span className="text-xs ml-2">{entry.buyer_name}</span>}
                    </button>
                    {tappedEntry?.id === entry.id && entry.buyer_name && (
                      <div className="absolute z-50 -top-8 left-1/2 -translate-x-1/2 px-3 py-1 bg-gray-900 text-white text-sm rounded shadow-lg whitespace-nowrap">{entry.buyer_name}</div>
                    )}
                  </div>
                );
              })}

              {/* Numeros individuales en grilla */}
              {raffle.entries.some(e => e.status === statusFilter && !e.display_numbers) && (
                <div className="grid grid-cols-5 xs:grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-15 gap-1.5 sm:gap-2 pt-2">
                  {raffle.entries.filter(e => e.status === statusFilter && !e.display_numbers).map((entry) => {
                    const isSelected = selectedEntries.find(e => e.id === entry.id);
                    const statusClass = entry.status === 'available' ? 'raffle-number available' : entry.status === 'reserved' ? 'raffle-number reserved' : 'raffle-number paid';
                    return (
                      <div key={entry.id} className="relative">
                        <button
                          onClick={() => handleEntryClick(entry)}
                          className={`${statusClass} ${isSelected ? 'selected' : ''} w-full p-2 sm:p-3 rounded-lg font-semibold text-xs sm:text-sm min-w-[40px] aspect-square flex items-center justify-center`}
                          data-testid={`entry-${entry.number}`}
                        >
                          {entry.number}
                        </button>
                        {tappedEntry?.id === entry.id && entry.buyer_name && (
                          <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap">
                            {entry.buyer_name}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {raffle.entries.filter(e => e.status === statusFilter).length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No hay numeros {statusFilter === 'available' ? 'disponibles' : statusFilter === 'reserved' ? 'reservados' : 'pagados'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Carrito de Seleccion */}
        {selectedEntries.length > 0 && (
          <Card ref={cartRef} className="bg-white/95 backdrop-blur-lg mb-6" data-testid="selection-cart">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Tu Seleccion</span>
                <Badge variant="secondary">{selectedEntries.length} {selectedEntries.length === 1 ? 'numero' : 'numeros'}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {selectedEntries.map((entry) => (
                    <Badge key={entry.id} variant="outline" className="text-lg px-3 py-1">
                      {entry.number}
                      <button onClick={() => handleEntryClick(entry)} className="ml-2 text-red-500 hover:text-red-700">
                        <X size={16} />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-green-600">${totalPrice.toLocaleString('es-AR')}</span>
                  </div>
                </div>

                {!showForm ? (
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={handleCancel} className="flex-1" data-testid="cancel-button">Cancelar</Button>
                    <Button onClick={() => {
                      setShowForm(true);
                      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
                    }} className="flex-1 hover:opacity-90" style={{ backgroundColor: getPrimaryColor() }} data-testid="continue-button">
                      <ShoppingCart className="mr-2" size={20} />
                      Continuar
                    </Button>
                  </div>
                ) : (
                  <div ref={formRef} className="space-y-4 border-t pt-4">
                    <h3 className="text-lg font-semibold">Tus datos:</h3>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="name">Nombre y Apellido</Label>
                        <Input id="name" value={buyerData.name} onChange={(e) => setBuyerData({ ...buyerData, name: e.target.value })} placeholder="Juan Perez" data-testid="buyer-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="phone">Numero de Telefono</Label>
                        <Input id="phone" value={buyerData.phone} onChange={(e) => setBuyerData({ ...buyerData, phone: e.target.value })} placeholder="+54 9 11 xxxx-xxxx" data-testid="buyer-phone-input" />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Button variant="outline" onClick={handleCancel} className="flex-1" data-testid="cancel-form-button">Cancelar</Button>
                      <Button
                        onClick={handleReserve}
                        disabled={reserving}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="reserve-button"
                      >
                        <MessageCircle className="mr-2" size={20} />
                        {reserving ? 'Reservando...' : 'Reservar por WhatsApp'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pantalla de Reserva Exitosa */}
        {reservationSuccess && (
          <Card className="bg-white/95 backdrop-blur-lg mb-6" data-testid="reservation-success">
            <CardContent className="pt-8 pb-8">
              <div className="text-center space-y-5">
                <CheckCircle size={64} className="mx-auto text-green-500" />
                <h2 className="text-2xl font-bold text-green-700">Reserva realizada!</h2>
                <p className="text-gray-600">
                  Reservaste: <strong>{reservationSuccess.numbers.join(', ')}</strong>
                </p>
                <p className="text-gray-600">
                  Total: <strong className="text-green-600">${reservationSuccess.total.toLocaleString('es-AR')}</strong>
                </p>
                <div className="border-t pt-5 mt-5">
                  <p className="text-gray-700 mb-4">
                    Toca el boton para enviarnos los datos de tu reserva por WhatsApp:
                  </p>
                  <a
                    href={reservationSuccess.whatsappUrl}
                    className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all active:scale-95"
                    data-testid="whatsapp-link"
                  >
                    <MessageCircle size={24} />
                    Enviar por WhatsApp
                  </a>
                </div>
                <button
                  onClick={() => setReservationSuccess(null)}
                  className="text-sm text-gray-400 hover:text-gray-600 mt-4 block mx-auto"
                  data-testid="close-success"
                >
                  Cerrar
                </button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RaffleDetailPage;
